import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import {
  Zap,
  Play,
  Check,
  Settings as SettingsIcon,
  Image as ImageIcon,
  Type,
  BarChart3,
  Sparkles,
  Info
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', aspect: '16:9', color: '#FF0000' },
  { id: 'youtube_shorts', name: 'YouTube Shorts', aspect: '9:16', color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', aspect: '9:16', color: '#000000' },
  { id: 'instagram_reels', name: 'Instagram Reels', aspect: '9:16', color: '#E4405F' },
  { id: 'instagram_feed', name: 'Instagram Feed', aspect: '1:1', color: '#E4405F' },
  { id: 'facebook_landscape', name: 'Facebook (16:9)', aspect: '16:9', color: '#1877F2' },
  { id: 'facebook_vertical', name: 'Facebook (4:5)', aspect: '4:5', color: '#1877F2' }
];

const Optimizer = () => {
  const { user } = React.useContext(AuthContext);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState('');
  
  // Assets
  const [eligibleAssets, setEligibleAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [variants, setVariants] = useState([]);
  
  // Optimizer settings
  const [creatorSettings, setCreatorSettings] = useState(null);
  const [brandkits, setBrandkits] = useState([]);
  
  // Optimization config
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [brandingMode, setBrandingMode] = useState('auto');
  const [captionsMode, setCaptionsMode] = useState('auto');
  const [watermarkOn, setWatermarkOn] = useState(true);
  const [progressBarOn, setProgressBarOn] = useState(true);
  const [thumbnailMode, setThumbnailMode] = useState('auto');
  const [selectedBrandkit, setSelectedBrandkit] = useState('');
  
  // Batch mode
  const [batchMode, setBatchMode] = useState(false);
  const [selectedBatchAssets, setSelectedBatchAssets] = useState([]);
  
  // Settings
  const [optimizerEnabled, setOptimizerEnabled] = useState(true);
  const [planLimits, setPlanLimits] = useState(null);
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchCreators();
      fetchBrandkits();
      checkSettings();
      checkPlanLimits();
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (selectedCreator) {
      fetchEligibleAssets();
      fetchCreatorSettings();
    }
  }, [selectedCreator]);

  useEffect(() => {
    if (selectedAsset) {
      fetchVariants();
    }
  }, [selectedAsset]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/organizations`, { withCredentials: true });
      setOrganizations(response.data);
      if (response.data.length > 0) {
        setSelectedOrg(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchCreators = async () => {
    try {
      const response = await axios.get(`${API}/creators?org_id=${selectedOrg}`, { withCredentials: true });
      setCreators(response.data);
      if (response.data.length > 0) {
        setSelectedCreator(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch creators:', error);
    }
  };

  const fetchBrandkits = async () => {
    try {
      const response = await axios.get(`${API}/brandkits/${selectedOrg}`, { withCredentials: true });
      setBrandkits(response.data);
      const defaultBk = response.data.find(bk => bk.is_default);
      if (defaultBk) {
        setSelectedBrandkit(defaultBk.id);
      }
    } catch (error) {
      console.error('Failed to fetch brandkits:', error);
    }
  };

  const checkSettings = async () => {
    try {
      const response = await axios.get(`${API}/organizations`, { withCredentials: true });
      const org = response.data.find(o => o.id === selectedOrg);
      setOptimizerEnabled(org?.settings?.optimizer_enabled !== false);
    } catch (error) {
      console.error('Failed to check settings:', error);
    }
  };

  const checkPlanLimits = () => {
    const org = organizations.find(o => o.id === selectedOrg);
    const tier = org?.plan_tier || 'free';
    
    const limits = {
      free: { maxPlatforms: 1, manualMode: false, batchSize: 0 },
      creator: { maxPlatforms: 2, manualMode: true, batchSize: 2 },
      pro: { maxPlatforms: 3, manualMode: true, batchSize: 10 },
      agency: { maxPlatforms: 99, manualMode: true, batchSize: 99 },
      agency_elite: { maxPlatforms: 99, manualMode: true, batchSize: 99 }
    };
    
    setPlanLimits(limits[tier] || limits.free);
  };

  const fetchCreatorSettings = async () => {
    try {
      const response = await axios.get(`${API}/creator-settings/${selectedCreator}`, { withCredentials: true });
      setCreatorSettings(response.data);
      
      // Apply defaults from creator settings
      setBrandingMode(response.data.default_branding_mode);
      setCaptionsMode(response.data.default_captions_mode);
      setWatermarkOn(response.data.default_watermark_on);
      setProgressBarOn(response.data.default_progress_bar_on);
      setThumbnailMode(response.data.default_thumbnail_mode);
      setSelectedPlatforms(response.data.default_platform_targets || []);
    } catch (error) {
      console.error('Failed to fetch creator settings:', error);
    }
  };

  const fetchEligibleAssets = async () => {
    try {
      const response = await axios.get(`${API}/optimizer/eligible-assets/${selectedCreator}`, { withCredentials: true });
      setEligibleAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch eligible assets:', error);
    }
  };

  const fetchVariants = async () => {
    if (!selectedAsset) return;
    try {
      const response = await axios.get(`${API}/optimizer/variants/${selectedAsset.id}`, { withCredentials: true });
      setVariants(response.data);
    } catch (error) {
      console.error('Failed to fetch variants:', error);
    }
  };

  const handleGenerateVariants = async () => {
    if (!selectedAsset) {
      toast.error('Please select an asset');
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    if (planLimits && selectedPlatforms.length > planLimits.maxPlatforms) {
      toast.error(`Your plan allows max ${planLimits.maxPlatforms} platform(s)`);
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/optimizer/generate-variants`,
        {
          source_asset_id: selectedAsset.id,
          platforms: selectedPlatforms,
          branding_mode: brandingMode,
          captions_mode: captionsMode,
          watermark_on: watermarkOn,
          progress_bar_on: progressBarOn,
          thumbnail_mode: thumbnailMode,
          brandkit_id: selectedBrandkit || null
        },
        { withCredentials: true }
      );

      toast.success('Optimized variants generated!');
      fetchVariants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate variants');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchGenerate = async () => {
    if (selectedBatchAssets.length === 0) {
      toast.error('Please select assets');
      return;
    }

    if (planLimits && selectedBatchAssets.length > planLimits.batchSize) {
      toast.error(`Your plan allows batch processing of max ${planLimits.batchSize} assets`);
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/optimizer/batch-generate`,
        {
          asset_ids: selectedBatchAssets,
          platforms: selectedPlatforms,
          branding_mode: brandingMode,
          captions_mode: captionsMode,
          watermark_on: watermarkOn,
          progress_bar_on: progressBarOn,
          thumbnail_mode: thumbnailMode,
          brandkit_id: selectedBrandkit || null,
          organization_id: selectedOrg
        },
        { withCredentials: true }
      );

      toast.success('Batch optimization completed!');
      setSelectedBatchAssets([]);
      setBatchMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to batch generate');
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (platformId) => {
    if (selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platformId));
    } else {
      if (planLimits && selectedPlatforms.length >= planLimits.maxPlatforms) {
        toast.warning(`Your plan allows max ${planLimits.maxPlatforms} platform(s)`);
        return;
      }
      setSelectedPlatforms([...selectedPlatforms, platformId]);
    }
  };

  if (!optimizerEnabled) {
    return (
      <div className="p-8" data-testid="optimizer-disabled">
        <div className="glass rounded-2xl p-12 text-center">
          <Zap size={48} className="mx-auto mb-4 text-[#8A93A6]" />
          <h2 className="text-2xl font-bold text-white mb-2">Optimizer Disabled</h2>
          <p className="text-[#8A93A6]">
            Optimizer is disabled for this organization. An owner or admin can enable it in Settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="optimizer-page">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Optimizer Engine</h1>
        <p className="text-[#8A93A6] text-lg">Transform content into platform-ready variants</p>
      </div>

      {/* Organization & Creator Selection */}
      <div className="glass rounded-2xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Organization</label>
            <Select value={selectedOrg} onValueChange={setSelectedOrg}>
              <SelectTrigger data-testid="select-organization">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Creator</label>
            <Select value={selectedCreator} onValueChange={setSelectedCreator}>
              <SelectTrigger data-testid="select-creator">
                <SelectValue placeholder="Select creator" />
              </SelectTrigger>
              <SelectContent>
                {creators.map(creator => (
                  <SelectItem key={creator.id} value={creator.id}>{creator.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Plan Limits Info */}
        {planLimits && (
          <div className="mt-4 p-4 rounded-xl bg-[#3A7BFF]/10 border border-[#3A7BFF]/20">
            <div className="flex items-center gap-2 text-sm">
              <Info size={16} className="text-[#3A7BFF]" />
              <span className="text-white">
                Max {planLimits.maxPlatforms} platform(s) per asset
                {planLimits.batchSize > 0 && ` • Batch: up to ${planLimits.batchSize} assets`}
                {!planLimits.manualMode && ' • Auto mode only'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Batch Mode Toggle */}
      {planLimits && planLimits.batchSize > 0 && (
        <div className="flex items-center justify-between glass rounded-xl p-4">
          <span className="text-white font-medium">Batch Optimization Mode</span>
          <Switch checked={batchMode} onCheckedChange={setBatchMode} />
        </div>
      )}

      <Tabs defaultValue="single" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="single" disabled={batchMode}>Single Asset</TabsTrigger>
          <TabsTrigger value="batch" disabled={!batchMode || (planLimits && planLimits.batchSize === 0)}>
            Batch Mode
          </TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Asset Selection */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Select Asset</h2>
              <div className="space-y-3">
                {eligibleAssets.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => setSelectedAsset(asset)}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      selectedAsset?.id === asset.id
                        ? 'border-2 border-[#3A7BFF] bg-[#3A7BFF]/10'
                        : 'border border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`asset-${asset.id}`}
                  >
                    <p className="text-white font-medium text-sm">{asset.metadata_json?.clip_name || 'Asset'}</p>
                    <p className="text-xs text-[#8A93A6]">{asset.type}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Middle: Platform & Config */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Platforms & Settings</h2>
              
              {/* Platforms */}
              <div className="space-y-2 mb-6">
                <label className="text-sm text-[#8A93A6]">Target Platforms</label>
                <div className="grid grid-cols-2 gap-2">
                  {PLATFORMS.map(platform => (
                    <div
                      key={platform.id}
                      onClick={() => togglePlatform(platform.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedPlatforms.includes(platform.id)
                          ? 'border-[#3A7BFF] bg-[#3A7BFF]/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`platform-${platform.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={selectedPlatforms.includes(platform.id)} />
                        <span className="text-white text-xs">{platform.name}</span>
                      </div>
                      <span className="text-[#8A93A6] text-xs">{platform.aspect}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#8A93A6] mb-2">Branding Mode</label>
                  <Select value={brandingMode} onValueChange={setBrandingMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      {planLimits?.manualMode && <SelectItem value="manual">Manual</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-[#8A93A6] mb-2">Captions</label>
                  <Select value={captionsMode} onValueChange={setCaptionsMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <label className="flex items-center gap-2 text-sm text-white">
                  <Checkbox checked={watermarkOn} onCheckedChange={setWatermarkOn} />
                  Watermark
                </label>

                <label className="flex items-center gap-2 text-sm text-white">
                  <Checkbox checked={progressBarOn} onCheckedChange={setProgressBarOn} />
                  Progress Bar
                </label>

                {brandkits.length > 0 && (
                  <div>
                    <label className="text-sm text-[#8A93A6] mb-2">BrandKit</label>
                    <Select value={selectedBrandkit} onValueChange={setSelectedBrandkit}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select BrandKit" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandkits.map(bk => (
                          <SelectItem key={bk.id} value={bk.id}>
                            {bk.name} {bk.is_default ? '(Default)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <button
                  onClick={handleGenerateVariants}
                  disabled={loading || !selectedAsset || selectedPlatforms.length === 0}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="generate-variants-button"
                >
                  <Zap size={18} className="mr-2" />
                  Generate Variants
                </button>
              </div>
            </div>

            {/* Right: Generated Variants */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Generated Variants</h2>
              {variants.length === 0 ? (
                <div className="h-64 flex items-center justify-center border-2 border-dashed border-white/10 rounded-xl">
                  <p className="text-[#8A93A6] text-sm">No variants yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {variants.map((variant) => (
                    <div key={variant.id} className="p-4 rounded-xl border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Play size={16} className="text-[#3A7BFF]" />
                        <span className="text-white text-sm font-medium">
                          {PLATFORMS.find(p => p.id === variant.platform_target)?.name}
                        </span>
                      </div>
                      <p className="text-xs text-[#8A93A6]">{variant.aspect_ratio}</p>
                      {variant.ai_metadata_json?.ai_title && (
                        <p className="text-xs text-[#39E6C8] mt-2">{variant.ai_metadata_json.ai_title}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="batch" className="space-y-6">
          <div className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">Batch Optimization</h2>
            <p className="text-[#8A93A6] mb-4">
              Select multiple assets to optimize with the same settings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {eligibleAssets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => {
                    if (selectedBatchAssets.includes(asset.id)) {
                      setSelectedBatchAssets(selectedBatchAssets.filter(id => id !== asset.id));
                    } else {
                      setSelectedBatchAssets([...selectedBatchAssets, asset.id]);
                    }
                  }}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    selectedBatchAssets.includes(asset.id)
                      ? 'border-2 border-[#3A7BFF] bg-[#3A7BFF]/10'
                      : 'border border-white/10 hover:border-white/20'
                  }`}
                >
                  <Checkbox checked={selectedBatchAssets.includes(asset.id)} className="mb-2" />
                  <p className="text-white text-sm">{asset.metadata_json?.clip_name || 'Asset'}</p>
                  <p className="text-xs text-[#8A93A6]">{asset.type}</p>
                </div>
              ))}
            </div>

            <button
              onClick={handleBatchGenerate}
              disabled={loading || selectedBatchAssets.length === 0}
              className="btn-primary disabled:opacity-50"
              data-testid="batch-generate-button"
            >
              <Sparkles size={18} className="mr-2" />
              Generate for {selectedBatchAssets.length} Asset(s)
            </button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Optimizer;
